import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName } from '../../utils/lensDisplay';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.2rem, 2.2vw, 1.65rem);
  font-weight: 700;
  color: #fff;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToggleBtn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)')};
  background: ${p => (p.$active ? 'rgba(255,255,255,0.12)' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : 'rgba(255,255,255,0.45)')};
  font-size: 0.82rem;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const GroupBlock = styled.section`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
`;

const GroupHead = styled.div`
  padding: 12px 16px;
  font-size: clamp(0.95rem, 1.5vw, 1.05rem);
  font-weight: 700;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  &:last-child {
    border-bottom: none;
  }
`;

const RowBtn = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.88);
  font-size: clamp(0.9rem, 1.4vw, 1rem);
  text-align: left;
  cursor: ${p => (p.$expandable ? 'pointer' : 'default')};
  line-height: 1.4;
  &:hover {
    background: ${p => (p.$expandable ? 'rgba(255,255,255,0.04)' : 'none')};
  }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
  margin-top: 7px;
`;

const Meta = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
  display: block;
  margin-top: 4px;
`;

const Desc = styled.div`
  padding: 0 16px 12px 36px;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.45;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: rgba(255, 255, 255, 0.3);
`;

function ExpandableRow({ item, color, showDescription, typeLabel }) {
  const [open, setOpen] = useState(false);
  const hasDesc = showDescription && !!item.description;

  return (
    <Row>
      <RowBtn
        type="button"
        $expandable={hasDesc}
        onClick={() => hasDesc && setOpen(!open)}
      >
        <Dot $color={color} />
        <span style={{ flex: 1 }}>
          {item.title}
          <Meta>
            {typeLabel}
            {item.pivotLabel ? ` · ${item.pivotLabel}` : ''}
          </Meta>
        </span>
        {hasDesc && (
          <span style={{ color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        )}
      </RowBtn>
      {open && hasDesc && <Desc>{item.description}</Desc>}
    </Row>
  );
}

function pivotLabelForItem(item, pivotPoints, pivotEdits) {
  const pid = item.pivot_point_id;
  const edit = pivotEdits[pid];
  const pp = pivotPoints.find(p => p.id === pid);
  return edit?.title_override || pp?.title || '';
}

/**
 * Client-side grouped listing of all lens items (by lens or by type).
 */
export default function SummaryGroupedListView({
  items = [],
  lenses = [],
  pivotPoints = [],
  pivotEdits = {},
  showDescription = true,
}) {
  const { t, language } = useTranslation();
  const [mode, setMode] = useState('lens'); // 'lens' | 'type'

  const sortedLenses = useMemo(
    () => [...lenses].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [lenses]
  );

  const enriched = useMemo(
    () =>
      items.map(i => ({
        ...i,
        pivotLabel: pivotLabelForItem(i, pivotPoints, pivotEdits),
      })),
    [items, pivotPoints, pivotEdits]
  );

  const groupsLens = useMemo(() => {
    const map = new Map();
    sortedLenses.forEach(l => map.set(l.id, { key: l.id, label: lensDisplayName(l, language), color: l.color, items: [] }));
    enriched.forEach(i => {
      const g = map.get(i.lens_id);
      if (g) g.items.push(i);
    });
    return sortedLenses.map(l => map.get(l.id)).filter(g => g.items.length > 0);
  }, [enriched, sortedLenses, language]);

  const groupsType = useMemo(() => {
    const obs = enriched.filter(i => i.type === 'obstacle');
    const opp = enriched.filter(i => i.type === 'opportunity');
    const out = [];
    if (obs.length) out.push({ key: 'obstacle', label: t('lenses.obstacles'), color: COLORS.obstacle, items: obs });
    if (opp.length) out.push({ key: 'opportunity', label: t('lenses.opportunities'), color: COLORS.opportunity, items: opp });
    return out;
  }, [enriched, t]);

  const groups = mode === 'lens' ? groupsLens : groupsType;

  if (!items.length) {
    return (
      <Wrap>
        <Title>{t('lenses.listing')}</Title>
        <Empty>{t('lenses.noItemsToList')}</Empty>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Title>{t('lenses.listing')}</Title>
      <ToggleRow>
        <ToggleBtn type="button" $active={mode === 'lens'} onClick={() => setMode('lens')}>
          {t('lenses.groupByLens')}
        </ToggleBtn>
        <ToggleBtn type="button" $active={mode === 'type'} onClick={() => setMode('type')}>
          {t('lenses.groupByType')}
        </ToggleBtn>
      </ToggleRow>

      {groups.map(g => (
        <GroupBlock key={g.key}>
          <GroupHead>
            {g.key === 'obstacle' && <AlertTriangle size={18} color={g.color} />}
            {g.key === 'opportunity' && <Lightbulb size={18} color={g.color} />}
            {g.label}
            <span style={{ marginLeft: 'auto', fontWeight: 500, color: 'rgba(255,255,255,0.35)' }}>
              {g.items.length}
            </span>
          </GroupHead>
          <List>
            {g.items.map(item => (
              <ExpandableRow
                key={item.id}
                item={item}
                color={item.type === 'obstacle' ? COLORS.obstacle : COLORS.opportunity}
                showDescription={showDescription}
                typeLabel={item.type === 'obstacle' ? t('lenses.obstacle') : t('lenses.opportunity')}
              />
            ))}
          </List>
        </GroupBlock>
      ))}
    </Wrap>
  );
}
